import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUserProfile } from '../lib/auth/useUserProfile';
import { auth } from '../firebase';
import { useSchools } from '../lib/data/useSchools';
import { signOut } from 'firebase/auth';
import { Icons } from '../lib/Icons';
import { requestJoinSchool } from '../lib/functions';
import { ROLE_LABELS } from '../lib/auth/roles';

export default function PendingApprovalGate({ children }) {
  const { profile, loading } = useUserProfile();
  const [requested, setRequested] = useState(false);
  const { schools, loading: loadingSchools } = useSchools();
  const navigate = useNavigate();

  if (loading) return <div className="p-6 text-gray-600">Checking your account…</div>;

  // If profile missing, let the rest of app handle onboarding/login
  if (!profile) return children;

  // Solo users should not be on school pages - show friendly redirect
  if (profile.userType === 'solo' || profile.role === 'solo_user') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="bg-white shadow-xl rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icons.BookOpen className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">You're a Solo User</h1>
          <p className="text-gray-600 mb-6">
            This area is for school accounts. As an individual subscriber, you have access to our SEN resources library.
          </p>
          <button
            onClick={() => navigate('/solo/dashboard')}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl py-3 font-semibold hover:from-purple-600 hover:to-pink-600 transition-all mb-4"
          >
            Go to My Resources →
          </button>
          <button
            onClick={async () => {
              await signOut(auth);
              navigate('/login');
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Sign out and switch accounts
          </button>
        </div>
      </div>
    );
  }

  if (profile.isActive === true) return children;

  async function requestAccess(e) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const requestedSchoolId = String(form.get('schoolId') || '').trim();
    const requestedRole = String(form.get('role') || 'TEACHER').toUpperCase();
    if (!requestedSchoolId) return;
    try {
      await requestJoinSchool({ requestedSchoolId, requestedRole });
      setRequested(true);
    } catch (err) {
      console.error('Failed to submit join request:', err);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white shadow rounded-lg p-6 max-w-md w-full">
        <h1 className="text-xl font-semibold mb-2">Pending Approval</h1>
        <p className="text-sm text-gray-600">Your account is not yet active for a school. Submit a request below and a Full Access user (SENCO or Senior Leader) will approve access.</p>
        <form onSubmit={requestAccess} className="mt-4 space-y-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">School</label>
            <select name="schoolId" className="w-full border rounded p-2" defaultValue={profile.requestedSchoolId || ''} required>
              <option value="" disabled>{loadingSchools ? 'Loading schools…' : 'Select your school'}</option>
              {schools.map(s => (
                <option key={s.id} value={s.id}>{s.schoolName || s.id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Requested Role</label>
            <select name="role" className="w-full border rounded p-2" defaultValue={profile.requestedRole || 'TEACHER'}>
              <option value="TEACHER">{ROLE_LABELS.TEACHER}</option>
              <option value="TA">{ROLE_LABELS.TA}</option>
              <option value="SENCO">{ROLE_LABELS.SENCO}</option>
            </select>
          </div>
          <button className="w-full bg-indigo-600 text-white rounded p-2">Request Access</button>
          {requested && <div className="text-xs text-green-600">Request sent. Await approval by your school's SENCO or Senior Leader.</div>}
        </form>
        
        {/* Cross-navigation for users who might have a solo account */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Not part of a school?</p>
          <Link 
            to="/solo/dashboard" 
            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
          >
            Go to Individual Teacher Access →
          </Link>
        </div>
        
        <button
          onClick={async () => {
            await signOut(auth);
            navigate('/');
          }}
          className="mt-4 text-xs text-gray-400 hover:text-gray-600"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
